<template>
	<v-text-field
		ref="field"
		v-bind="{ ...kindProps[kind], ...searchFieldProps(modelValue, errorMessages) }"
		:model-value="modelValue"
		@update:model-value="emit('update:modelValue', $event)">
		<!-- Every v-text-field slot (append, message, …) passes straight through. -->
		<template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
			<slot :name="slotName" v-bind="slotProps ?? {}" />
		</template>
	</v-text-field>
</template>

<script setup lang="ts">
	import { ref } from "vue";
	import type { VTextField } from "vuetify/components";
	import { searchFieldProps } from "@/helpers/searchLimit";

	/**
	 * The search box admin uses everywhere: a v-text-field with one of two looks, plus the
	 * 250-character search limit from helpers/searchLimit.ts (counter and message appear only once a
	 * search runs over). Every other v-text-field prop, listener, class and directive passes through,
	 * and overrides the defaults below.
	 *
	 * - `page` (default): the pill search bar at the top of a list or page.
	 * - `filter`: the "Search" box inside a filter menu.
	 */
	type SearchFieldKind = "page" | "filter";

	const kindProps: Record<SearchFieldKind, Record<string, unknown>> = {
		page: { density: "compact", rounded: "pill", clearable: true, prependInnerIcon: "icon-search-magnifying-glass" },
		filter: { label: "Search", appendInnerIcon: "icon-search-magnifying-glass", singleLine: true }
	};

	withDefaults(
		defineProps<{
			modelValue?: string | null;
			kind?: SearchFieldKind;
			/** Merged after the over-limit message, so a caller's own errors still show. */
			errorMessages?: string | string[];
		}>(),
		{ modelValue: "", kind: "page", errorMessages: () => [] }
	);
	const emit = defineEmits<{ "update:modelValue": [value: string | null] }>();

	const field = ref<InstanceType<typeof VTextField> | null>(null);

	// Callers hold a ref to this component and call focus()/blur() or read $el, as they did on the
	// v-text-field it replaces.
	defineExpose({
		focus: () => field.value?.focus(),
		blur: () => field.value?.blur()
	});
</script>
